import i18n from "../i18n";
import { BookingStatus, TimeSlot, TripType } from "@common/types";
import type { Trip } from "@common/types";
import {
  allowedSlotsForTrip,
  bookingInterval,
  bookingStartMinutes,
  slotForStart,
  formatClock,
  formatMinutes,
  startMinutes,
  type OverlapBooking,
  type OverlapTrip,
} from "@common/booking-overlap";

export interface SlotBooking {
  id: string;
  tripId: string;
  date?: string;
  timeSlot?: string;
  status: string;
  customStartTime?: string;
  customDurationHours?: number;
  tripDurationMinutes?: number;
  tripName?: string;
  /** From the server: when the vessel is taken, minutes after midnight, buffers included */
  occupiedFrom?: number;
  occupiedTo?: number;
}

/** Another departure that keeps the vessel busy, ready to show. */
export interface VesselConflict {
  bookingId?: string;
  tripName?: string;
  from: string;
  to: string;
}

/**
 * Time slots the selected trip cannot take another booking in on a date.
 * Mirrors the server (findSlotConflict): a slot holds at most trip.maxBookingsPerDay
 * bookings of the same trip, a full-day trip needs an empty day, and an existing
 * full-day booking (when `allTrips` is given) blocks the whole day.
 * Bookings of other trips never block a slot.
 */
export function unavailableSlots(
  bookings: SlotBooking[],
  trip: Pick<Trip, "id" | "tripType" | "maxBookingsPerDay"> | undefined,
  date: string,
  allTrips: Pick<Trip, "id" | "tripType">[] = [],
): string[] {
  if (!trip) return [];
  const active = bookings.filter(
    (b) => (b.date === undefined || b.date === date) && b.status !== BookingStatus.CANCELLED,
  );
  const all = allowedSlotsForTrip(trip) as string[];
  const fullDayIds = new Set(allTrips.filter((t) => t.tripType === TripType.FULL_DAY).map((t) => t.id));
  if (active.some((b) => fullDayIds.has(b.tripId))) return all;
  if (trip.tripType === TripType.FULL_DAY) return active.length > 0 ? all : [];
  return all.filter(
    (slot) => active.filter((b) => b.tripId === trip.id && b.timeSlot === slot).length >= trip.maxBookingsPerDay,
  );
}

const tripMapOf = (trips: Pick<Trip, "id">[]) => new Map(trips.map((t) => [t.id, t as unknown as OverlapTrip]));

/**
 * The first other departure that keeps the vessel busy for a booking of `trip`, if any.
 * An existing booking's interval is the server's occupiedFrom/occupiedTo when present (buffers
 * included; the only source for trips a partner cannot read), else computed from its trip and
 * stored duration. A booking of the same trip at the same start shares the departure.
 * With a custom start time the stored slot is derived like the server does.
 */
export function vesselConflict(
  bookings: SlotBooking[],
  trip: (Pick<Trip, "id"> & Partial<OverlapTrip>) | undefined,
  date: string,
  allTrips: Pick<Trip, "id">[],
  choice: { timeSlot: string; customStartTime?: string; customDurationHours?: number },
  ignoreBookingId?: string,
): VesselConflict | undefined {
  if (!trip) return undefined;
  const tripsById = tripMapOf(allTrips);
  tripsById.set(trip.id, trip as unknown as OverlapTrip);
  const custom = choice.customStartTime && startMinutes(choice.customStartTime) !== null;
  const mine: OverlapBooking = {
    id: ignoreBookingId,
    tripId: trip.id,
    date,
    tripDurationMinutes: trip.durationMinutes,
    ...choice,
    timeSlot: custom ? slotForStart(choice.customStartTime as string) : choice.timeSlot,
  };
  const myInterval = bookingInterval(mine, tripsById);
  const myStart = bookingStartMinutes(mine);
  if (!myInterval || myStart === null) return undefined;
  for (const other of bookings) {
    if (other.status === BookingStatus.CANCELLED) continue;
    if (other.date !== undefined && other.date !== date) continue;
    if (ignoreBookingId && other.id === ignoreBookingId) continue;
    const otherStart = bookingStartMinutes({ timeSlot: other.timeSlot ?? "", customStartTime: other.customStartTime });
    if (otherStart === null) continue;
    if (other.tripId === trip.id && otherStart === myStart && other.timeSlot === mine.timeSlot) continue; // shared departure, as on the server
    const known = typeof other.occupiedFrom === "number" && typeof other.occupiedTo === "number";
    const theirs: [number, number] | null = known
      ? [other.occupiedFrom as number, other.occupiedTo as number]
      : bookingInterval({ ...other, date, timeSlot: other.timeSlot ?? "" } as OverlapBooking, tripsById);
    if (!theirs) continue;
    if (myInterval[0] < theirs[1] && theirs[0] < myInterval[1]) {
      return {
        bookingId: other.id,
        tripName: other.tripName ?? tripsById.get(other.tripId)?.name,
        from: formatMinutes(theirs[0]),
        to: formatMinutes(theirs[1]),
      };
    }
  }
  return undefined;
}

/** Vessel conflicts per start slot of the trip (only slots that have one). */
export function slotConflicts(
  bookings: SlotBooking[],
  trip: (Pick<Trip, "id" | "tripType"> & Partial<OverlapTrip>) | undefined,
  date: string,
  allTrips: Pick<Trip, "id">[],
  ignoreBookingId?: string,
): Record<string, VesselConflict> {
  const result: Record<string, VesselConflict> = {};
  if (!trip) return result;
  for (const slot of allowedSlotsForTrip(trip as { tripType?: TripType; timeSlots?: TimeSlot[] })) {
    const conflict = vesselConflict(bookings, trip, date, allTrips, { timeSlot: slot }, ignoreBookingId);
    if (conflict) result[slot] = conflict;
  }
  return result;
}

export type ClockLocale = "sv" | "en";

/** The UI language as sv | en (anything not Swedish is English). */
export function currentClockLocale(): ClockLocale {
  return (i18n.language ?? "").toLowerCase().startsWith("sv") ? "sv" : "en";
}

/** Label for a start slot in the language's clock style; the evening slot also gets its word. */
export function slotLabel(slot: string, t: (key: string) => string, locale: ClockLocale = currentClockLocale()): string {
  const clock = formatClock(slot, locale);
  return slot === TimeSlot.EVENING ? `${t("slotScheduling.evening")} ${clock}` : clock;
}

/** "18:30 - 21:30 + 30 min efter" for every start slot the trip offers. */
export function occupiedSummary(
  trip: { tripType?: TripType; timeSlots?: TimeSlot[]; durationMinutes: number; bufferBeforeMinutes?: number; bufferAfterMinutes?: number },
  t: (key: string, o?: Record<string, unknown>) => string,
  locale: ClockLocale = currentClockLocale(),
): string {
  return allowedSlotsForTrip(trip)
    .map((slot) => {
      const start = startMinutes(slot) ?? 0;
      const before = trip.bufferBeforeMinutes ? t("slotScheduling.editor.before", { n: trip.bufferBeforeMinutes }) : "";
      const after = trip.bufferAfterMinutes ? t("slotScheduling.editor.after", { n: trip.bufferAfterMinutes }) : "";
      return `${before}${formatClock(formatMinutes(start), locale)} - ${formatClock(formatMinutes(start + trip.durationMinutes), locale)}${after}`;
    })
    .join("; ");
}
